import { Reducer } from "redux";

import { CustomField, CustomFieldCount } from "@/Contracts/Settings";

import {
	CustomFieldsActions,
	CustomFieldsActionTypes,
	CountCustomFieldsActionTypes,
	CustomFieldsOperationActionTypes,
	CustomFieldsEntityHydrationTypes,
	AllCustomFieldsSuccessfulAction
} from "@/Actions/CustomFields";
import { ZomentumEntities } from "@/Contracts";
import { filterOutFormulaFields } from "@/Utils";

export interface CustomFieldsState {
	readonly customFieldList: {
		address: CustomField[];
		opportunity: CustomField[];
		client_company: CustomField[];
		client_user: CustomField[];
		document: CustomField[];
		item: CustomField[];
		log_call: CustomField[];
	};
	// This is used to store custom fields without formula fields. It helps in avoiding filtering out formula fields multiple times
	readonly customFieldListWithoutFormulaFields: {
		address: CustomField[];
		opportunity: CustomField[];
		client_company: CustomField[];
		document: CustomField[];
		item: CustomField[];
	};
	readonly customFieldCount: CustomFieldCount;
	readonly customFieldIdMapping: Record<string, unknown>;
	readonly customFieldEntityHydrated: {
		address: boolean;
		opportunity: boolean;
		client_company: boolean;
		client_user: boolean;
		document: boolean;
		item: boolean;
		log_call: boolean;
	};
}

export const initialCustomFieldsState: CustomFieldsState = {
	customFieldList: {
		address: new Array<CustomField>(),
		opportunity: new Array<CustomField>(),
		client_company: new Array<CustomField>(),
		client_user: new Array<CustomField>(),
		document: new Array<CustomField>(),
		item: new Array<CustomField>(),
		log_call: new Array<CustomField>()
	},
	customFieldListWithoutFormulaFields: {
		address: new Array<CustomField>(),
		opportunity: new Array<CustomField>(),
		client_company: new Array<CustomField>(),
		document: new Array<CustomField>(),
		item: new Array<CustomField>()
	},
	customFieldCount: new CustomFieldCount(),
	customFieldIdMapping: {},
	customFieldEntityHydrated: {
		address: false,
		opportunity: false,
		client_company: false,
		client_user: false,
		document: false,
		item: false,
		log_call: false
	}
};

export const customFieldIdMapping = (
	customFields: CustomField[]
): Record<string, unknown> => {
	const reverseIdCustomFieldMapping: Record<string, unknown> = {};

	Object.entries(customFields).forEach(([_, value]: any) => {
		reverseIdCustomFieldMapping[value.id] = value;
	});

	return reverseIdCustomFieldMapping;
};

export const CustomFieldsReducer: Reducer<
	CustomFieldsState,
	CustomFieldsActions
> = (
	state: CustomFieldsState = initialCustomFieldsState,
	action: CustomFieldsActions
): CustomFieldsState => {
	switch (action.type) {
		case CustomFieldsActionTypes.ADDRESS_CUSTOM_FIELDS_SUCCESSFUL: {
			const addressIdMapping = customFieldIdMapping(
				action.customFieldList
			);

			return {
				...state,
				customFieldList: {
					...state.customFieldList,
					address: action.customFieldList
				},
				customFieldListWithoutFormulaFields: {
					...state.customFieldListWithoutFormulaFields,
					address: filterOutFormulaFields(action.customFieldList)
				},
				customFieldIdMapping: {
					...state.customFieldIdMapping,
					...addressIdMapping
				},
				customFieldEntityHydrated: {
					...state.customFieldEntityHydrated,
					address: true
				}
			};
		}

		case CustomFieldsActionTypes.OPPORTUNITY_CUSTOM_FIELDS_SUCCESSFUL: {
			const opportunityIdMapping = customFieldIdMapping(
				action.customFieldList
			);

			return {
				...state,
				customFieldList: {
					...state.customFieldList,
					opportunity: action.customFieldList
				},
				customFieldListWithoutFormulaFields: {
					...state.customFieldListWithoutFormulaFields,
					opportunity: filterOutFormulaFields(action.customFieldList)
				},
				customFieldIdMapping: {
					...state.customFieldIdMapping,
					...opportunityIdMapping
				}
			};
		}

		case CustomFieldsActionTypes.CLIENTS_CUSTOM_FIELDS_SUCCESSFUL: {
			const clientIdMapping = customFieldIdMapping(
				action.customFieldList
			);
			return {
				...state,
				customFieldList: {
					...state.customFieldList,
					client_company: action.customFieldList
				},
				customFieldListWithoutFormulaFields: {
					...state.customFieldListWithoutFormulaFields,
					client_company: filterOutFormulaFields(
						action.customFieldList
					)
				},
				customFieldIdMapping: {
					...state.customFieldIdMapping,
					...clientIdMapping
				}
			};
		}

		case CustomFieldsActionTypes.CONTACTS_CUSTOM_FIELDS_SUCCESSFUL: {
			const contactsIdMapping = customFieldIdMapping(
				action.customFieldList
			);
			return {
				...state,
				customFieldList: {
					...state.customFieldList,
					client_user: action.customFieldList
				},
				customFieldIdMapping: {
					...state.customFieldIdMapping,
					...contactsIdMapping
				}
			};
		}

		case CustomFieldsActionTypes.DOCUMENTS_CUSTOM_FIELDS_SUCCESSFUL: {
			const documentIdMapping = customFieldIdMapping(
				action.customFieldList
			);
			return {
				...state,
				customFieldList: {
					...state.customFieldList,
					document: action.customFieldList
				},
				customFieldListWithoutFormulaFields: {
					...state.customFieldListWithoutFormulaFields,
					document: filterOutFormulaFields(action.customFieldList)
				},
				customFieldIdMapping: {
					...state.customFieldIdMapping,
					...documentIdMapping
				}
			};
		}

		case CustomFieldsActionTypes.PRODUCTS_CUSTOM_FIELDS_SUCCESSFUL: {
			const productIdMapping = customFieldIdMapping(
				action.customFieldList
			);
			return {
				...state,
				customFieldList: {
					...state.customFieldList,
					item: action.customFieldList
				},
				customFieldListWithoutFormulaFields: {
					...state.customFieldListWithoutFormulaFields,
					item: filterOutFormulaFields(action.customFieldList)
				},
				customFieldIdMapping: {
					...state.customFieldIdMapping,
					...productIdMapping
				}
			};
		}

		case CustomFieldsActionTypes.CALL_LOGS_CUSTOM_FIELDS_SUCCESSFUL: {
			const callLogsIdMapping = customFieldIdMapping(
				action.customFieldList
			);
			return {
				...state,
				customFieldList: {
					...state.customFieldList,
					log_call: action.customFieldList
				},
				customFieldIdMapping: {
					...state.customFieldIdMapping,
					...callLogsIdMapping
				}
			};
		}

		case CountCustomFieldsActionTypes.COUNT_CUSTOM_FIELD_LIST_SUCCESSFUL: {
			return {
				...state,
				customFieldCount: action.customFieldCount
			};
		}

		case CustomFieldsOperationActionTypes.REORDER_CUSTOM_FIELDS_SUCCESSFUL: {
			return {
				...state,
				customFieldList: {
					...state.customFieldList,
					[action.entityType]: action.customFieldList
				}
			};
		}

		case CustomFieldsEntityHydrationTypes.ADDRESS_HYDRATE: {
			return {
				...state,
				customFieldEntityHydrated: {
					...state.customFieldEntityHydrated,
					address: action.customFieldEntityHydrated
				}
			};
		}

		case CustomFieldsEntityHydrationTypes.OPPORTUNITY_HYDRATE: {
			return {
				...state,
				customFieldEntityHydrated: {
					...state.customFieldEntityHydrated,
					opportunity: action.customFieldEntityHydrated
				}
			};
		}

		case CustomFieldsEntityHydrationTypes.DOCUMENTS_HYDRATE: {
			return {
				...state,
				customFieldEntityHydrated: {
					...state.customFieldEntityHydrated,
					document: action.customFieldEntityHydrated
				}
			};
		}

		case CustomFieldsEntityHydrationTypes.CLIENTS_HYDRATE: {
			return {
				...state,
				customFieldEntityHydrated: {
					...state.customFieldEntityHydrated,
					client_company: action.customFieldEntityHydrated
				}
			};
		}

		case CustomFieldsEntityHydrationTypes.CALL_LOGS_HYDRATE: {
			return {
				...state,
				customFieldEntityHydrated: {
					...state.customFieldEntityHydrated,
					log_call: action.customFieldEntityHydrated
				}
			};
		}

		case CustomFieldsEntityHydrationTypes.CONTACTS_HYDRATE: {
			return {
				...state,
				customFieldEntityHydrated: {
					...state.customFieldEntityHydrated,
					client_user: action.customFieldEntityHydrated
				}
			};
		}

		case CustomFieldsEntityHydrationTypes.PRODUCTS_HYDRATE: {
			return {
				...state,
				customFieldEntityHydrated: {
					...state.customFieldEntityHydrated,
					item: action.customFieldEntityHydrated
				}
			};
		}

		case CustomFieldsActionTypes.ALL_CUSTOM_FIELDS_SUCCESSFUL: {
			const allFields = (action as AllCustomFieldsSuccessfulAction)
				.customFieldList;

			const grouped: Record<string, CustomField[]> = {};
			allFields.forEach(field => {
				const key = field.entity_type;
				if (!grouped[key]) grouped[key] = [];
				grouped[key].push(field);
			});

			const address = grouped[ZomentumEntities.Address] ?? [];
			const opportunity = grouped[ZomentumEntities.Opportunity] ?? [];
			const client_company = grouped[ZomentumEntities.Client] ?? [];
			const client_user = grouped[ZomentumEntities.ClientContact] ?? [];
			const document = grouped[ZomentumEntities.Document] ?? [];
			const item = grouped[ZomentumEntities.Product] ?? [];
			const log_call = grouped[ZomentumEntities.SalesLogCall] ?? [];

			const allFieldsFlat = [
				...address,
				...opportunity,
				...client_company,
				...client_user,
				...document,
				...item,
				...log_call
			];

			return {
				...state,
				customFieldList: {
					address,
					opportunity,
					client_company,
					client_user,
					document,
					item,
					log_call
				},
				customFieldListWithoutFormulaFields: {
					address: filterOutFormulaFields(address),
					opportunity: filterOutFormulaFields(opportunity),
					client_company: filterOutFormulaFields(client_company),
					document: filterOutFormulaFields(document),
					item: filterOutFormulaFields(item)
				},
				customFieldIdMapping: customFieldIdMapping(allFieldsFlat),
				customFieldEntityHydrated: {
					...state.customFieldEntityHydrated,
					address: true
				}
			};
		}

		default: {
			return state;
		}
	}
};
